import cn from 'classnames'
import dynamic from 'next/dynamic'
import React, { MutableRefObject } from 'react'

import { ContextValue } from '@/context/Cities'
import { useViewport } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { ListingCate } from '@/page-commons/listingBySlug'
import { isEnter } from '@/utils/keyboard-helper'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleListingDetailClick } from '@/utils/tracking/eventsHandlers'

import { mobileImageGalleryBox } from './styles.css'

const DynamicModalMosaic = dynamic(() => import('@/components/Modal/Types/Mosaic').then((module) => module.ModalMosaic))
const DynamicPondImageGallery = dynamic(() => import('../PondImageGallery').then((module) => module.PondImageGallery))

type MobileImageGalleryProps = {
  cities: ContextValue
  citySlug: string
  eventCategory: GTMEventCategory
  handleUnlock?: VoidFunction
  photos: ListingCate['photos']
  planimetryUrl: string
  swipeCallback?: (index: number) => void
  title: string
}

export const MobileImageGallery: React.FC<MobileImageGalleryProps> = ({
  cities,
  citySlug,
  eventCategory,
  handleUnlock,
  photos,
  planimetryUrl,
  swipeCallback,
  title,
}) => {
  const { width: innerWidth } = useViewport()
  const { closeModal, openModal } = useModal()

  const imageMosaicHeight = (innerWidth / 4) * 3
  const isLocked = !!handleUnlock

  const handleGalleryClick = (event: React.SyntheticEvent<HTMLElement>) => {
    if (isLocked) {
      handleUnlock()
      return
    }

    event.preventDefault()
    openModal(
      <DynamicModalMosaic
        cities={cities}
        citySlug={citySlug}
        closeModal={closeModal}
        eventCategory={eventCategory}
        height={imageMosaicHeight}
        photos={photos}
        planimetryUrl={planimetryUrl}
        title={title}
      />
    )

    handleListingDetailClick('OpenGallery')
  }

  return (
    <>
      <div
        className={cn(mobileImageGalleryBox)}
        data-testid="mobile-gallery-open-mosaic"
        role="button"
        tabIndex={0}
        onClick={handleGalleryClick}
        onKeyPress={(e) => {
          if (isEnter(e)) {
            handleGalleryClick(e)
          }
        }}
      >
        {innerWidth > 0 && (
          <DynamicPondImageGallery
            innerWidth={innerWidth}
            isBlurred={isLocked}
            items={photos}
            swipeCallback={swipeCallback}
            title={title}
          />
        )}
      </div>
    </>
  )
}
